import React from "react";
import { ChevronDown, Eye, Loader2, Play, Pause, ShieldCheck, AlertCircle } from "lucide-react";
import { formatDateTimeET } from "../../utils/datetime";
import StrategySymbolChart from "./StrategySymbolChart";
import Modal from "../Modal";
import StrategyConfigPanel from "../StrategyConfigPanel";
import {
    getAdaptiveLevelTone,
    hasFreshLevels,
    isCurrentNySessionDate,
    isEarlyRegularSession,
    isRegularMarketOpen,
    isValidPriceLevel,
} from "./strategyStatusUtils";
import LiveStrategyPrice from "./LiveStrategyPrice";
import { useQuote } from "../../market/QuoteStore";

interface DTStrategyContentProps {
    dtStatus: any;
    handleStrategyToggle: (strategyName: "ON" | "ORB" | "DT", currentlyEnabled: boolean | undefined) => Promise<void>;
    strategyToggleLoading: Partial<Record<"ON" | "ORB" | "DT", "starting" | "pausing">>;
}

interface DTSymbolRowProps {
    symbol: string;
    status: any;
    levelsDateIso?: string;
    expanded: boolean;
    onToggle: () => void;
    chartWidth?: number;
    marketOpen: boolean;
}

const DTSymbolRow = React.memo<DTSymbolRowProps>(({
    symbol,
    status,
    levelsDateIso,
    expanded,
    onToggle,
    chartWidth,
    marketOpen,
}) => {
    const quoteIdentifier = status?.evaluated_quote?.local_symbol || symbol;
    const streamedQuote = useQuote(quoteIdentifier);
    const liveStatusPrice = Number(
        streamedQuote?.price ?? status.live_quote?.price ?? status.current_price
    );
    const dailyOpen = Number(status.daily_open);
    const threshold = Number(status.intraday_threshold);
    const hasCurrentLevels = hasFreshLevels(status, levelsDateIso)
        && isCurrentNySessionDate(status, levelsDateIso)
        && isValidPriceLevel(liveStatusPrice)
        && isValidPriceLevel(dailyOpen)
        && Number.isFinite(threshold)
        && threshold > 0;
    const levelTone = getAdaptiveLevelTone({
        price: liveStatusPrice,
        lowerLevel: dailyOpen - threshold,
        upperLevel: dailyOpen + threshold,
        levelsReady: hasCurrentLevels,
        earlySession: isEarlyRegularSession(),
        marketOpen,
    });
    return (
        <>
        <tr
            className="cursor-pointer transition-colors hover:bg-white/5"
            style={levelTone.rowStyle}
            title={levelTone.label}
            onClick={onToggle}
        >
            <td className="px-4 py-3 font-bold text-[14px] text-white">
                <div className="flex items-center gap-2">
                    <ChevronDown className={`w-3.5 h-3.5 text-muted transition-transform ${expanded ? "rotate-180 text-neon" : ""}`} />
                    <div>
                        <span>{symbol}</span>
                        <span className={`block text-[10px] font-medium ${levelTone.priceClass}`}>{levelTone.label}</span>
                        {status.execution_sec_type === "FUT" && status.execution_display_symbol && (
                            <span className="block text-[10px] font-mono font-normal text-amber-300/80">
                                → {status.execution_display_symbol}
                            </span>
                        )}
                    </div>
                </div>
            </td>
            <td className={`px-4 py-3 text-right font-mono ${levelTone.priceClass}`}>
                <LiveStrategyPrice
                    symbol={symbol}
                    evaluatedPrice={status.current_price}
                    evaluatedQuote={status.evaluated_quote}
                    initialLiveQuote={status.live_quote}
                    decisionPrice={status.entry_type === "bar_close" ? status.decision_price : null}
                    decisionSource={status.decision_price_source}
                    decisionLabel={status.decision_label || (status.decision_price_source === "confirmed_5m_bar_close" ? "5m close" : "30m close")}
                    streamedQuoteOverride={streamedQuote}
                    priceClassName={levelTone.priceClass}
                />
            </td>
            <td className="px-4 py-3 text-right font-mono">
                {status.daily_open ? `$${Number(status.daily_open).toFixed(2)}` : "—"}
            </td>
            <td className="px-4 py-3 text-right font-mono">
                {status.atr_value ? `$${Number(status.atr_value).toFixed(2)}` : "—"}
            </td>
            <td className={`px-4 py-3 text-right font-mono ${levelTone.upperClass}`}>
                {status.daily_open && status.intraday_threshold ? (
                    <>
                        <span className={levelTone.upperClass}>${(Number(status.daily_open) + Number(status.intraday_threshold)).toFixed(2)}</span>
                        <span className="mx-1 text-muted">/</span>
                        <span className={levelTone.lowerClass}>${(Number(status.daily_open) - Number(status.intraday_threshold)).toFixed(2)}</span>
                    </>
                ) : "—"}
            </td>
            <td className="px-4 py-3 text-right font-mono">
                {status.current_vwap ? `$${Number(status.current_vwap).toFixed(2)}` : "—"}
            </td>
            <td className="px-4 py-3 text-white/70">{status.threshold_window || "—"}</td>
            <td className="px-4 py-3 text-white/60 font-mono text-[12px]">
                {status.last_entry_time ? status.last_entry_time.slice(0, 5) : "—"}
            </td>
            <td className="px-4 py-3 text-white/70 text-xs uppercase">{status.execution_mode || "—"}</td>
            <td className="px-4 py-3 text-right font-mono text-white/80">
                {status.sl_atr_factor ? status.sl_atr_factor.toFixed(2) : "—"}
            </td>
            <td className="px-4 py-3 text-right font-mono text-white/80">
                {status.calculated_size === 0 && Number(status.leverage) > 0 ? (
                    <span className="inline-flex items-center gap-1 text-amber-400 font-semibold" title="Position size is 0 (Equity too low for 1 share with current leverage)">
                        <AlertCircle size={12} className="inline text-amber-400 shrink-0" />
                        0
                    </span>
                ) : (
                    status.calculated_size ?? "—"
                )}
                {status.execution_sec_type === 'FUT' && status.calculated_size !== undefined && status.calculated_size !== null && status.calculated_size !== 0 && (
                    <span className="ml-1 text-[10px] text-white/40">ct</span>
                )}
            </td>
            <td className="px-4 py-3 text-right font-mono text-white/80">
                {status.leverage ? `${status.leverage}x` : "—"}
            </td>
            <td className="px-4 py-3 text-right font-medium">
                <span className={(status.trades_today || 0) >= (status.max_trades || 1) ? "text-rose-400" : "text-muted"}>
                    {status.trades_today || 0}/{status.max_trades || 1}
                </span>
            </td>
            <td className="px-4 py-3 text-right font-medium">
                {status.position?.shares !== 0 ? (
                    <>
                        {status.position?.shares}
                        {status.execution_sec_type === 'FUT' && (
                            <span className="ml-1 text-[10px] text-white/40">ct</span>
                        )}
                    </>
                ) : "—"}
            </td>
        </tr>
        {expanded && (
            <tr>
                <td colSpan={14} className="p-0 bg-black/20">
                    <div
                        className="sticky left-0 box-border min-w-0 overflow-hidden"
                        style={chartWidth ? { width: `${chartWidth}px` } : undefined}
                    >
                        <StrategySymbolChart
                            symbol={symbol}
                            timeframe="30min"
                            label="ATR-DT"
                            strategy="DT"
                            status={{
                                ...status,
                                levels_date: status.levels_date || levelsDateIso,
                            }}
                        />
                    </div>
                </td>
            </tr>
        )}
        </>
    );
});

const DTStrategyContent: React.FC<DTStrategyContentProps> = ({
    dtStatus,
    handleStrategyToggle,
    strategyToggleLoading,
}) => {
    const [showRules, setShowRules] = React.useState(false);
    const [expandedSymbol, setExpandedSymbol] = React.useState<string | null>(null);
    const [showConfigModal, setShowConfigModal] = React.useState(false);
    const [marketOpen, setMarketOpen] = React.useState(() => isRegularMarketOpen());
    const tableViewportRef = React.useRef<HTMLDivElement>(null);
    const [chartWidth, setChartWidth] = React.useState<number>();
    const dtData = dtStatus?.data || ({} as any);

    React.useEffect(() => {
        const viewport = tableViewportRef.current;
        if (!viewport) return;
        const updateWidth = () => setChartWidth(Math.floor(viewport.clientWidth));
        updateWidth();
        const observer = new ResizeObserver(updateWidth);
        observer.observe(viewport);
        return () => observer.disconnect();
    }, []);

    React.useEffect(() => {
        const updateMarketState = () => setMarketOpen(isRegularMarketOpen());
        const interval = window.setInterval(updateMarketState, 30_000);
        return () => window.clearInterval(interval);
    }, []);

    if (!dtStatus) return null;

    const hasData = !!dtStatus?.data;
    const levelsDateIso = dtData?.levels_date as string | undefined;
    const levelsDate =
        levelsDateIso && !Number.isNaN(Date.parse(levelsDateIso))
            ? formatDateTimeET(levelsDateIso)
            : undefined;

    const dtEnabled = dtData?.enabled ?? false;
    const toggleState = strategyToggleLoading.DT;

    return (
        <>
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted">Strategy</span>
                    <span className="font-semibold text-foreground">ATR-DT</span>
                    {dtData?.version && (
                        <span className="px-1.5 py-0.5 rounded-md bg-white/5 border border-white/10 text-[10px] text-muted font-mono">
                            v{dtData.version}
                        </span>
                    )}
                </div>
                <button
                    onClick={() => handleStrategyToggle("DT", dtEnabled)}
                    disabled={!!toggleState}
                    aria-busy={!!toggleState}
                    className={`min-w-[104px] justify-center px-3 py-1.5 rounded-lg font-semibold uppercase flex text-sm items-center gap-1 transition disabled:cursor-wait disabled:opacity-80 ${toggleState ? "bg-amber-400/10 text-amber-300 border border-amber-400/20" : dtEnabled ? "bg-neon-soft text-neon" : "text-muted hover:text-foreground hover:bg-white/10"
                        }`}
                >
                    {toggleState ? <Loader2 size={14} className="animate-spin" /> : dtEnabled ? <Pause size={14} /> : <Play size={14} />}
                    {toggleState === "starting" ? "Starting" : toggleState === "pausing" ? "Pausing" : dtEnabled ? "Pause" : "Start"}
                </button>
            </div>

            <div className="text-foreground">
                <button
                    onClick={() => setShowRules(!showRules)}
                    className="flex items-center gap-2 mb-4 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-[11px] font-black uppercase tracking-[0.2em] text-muted hover:text-white hover:bg-white/10 transition-all shadow-inner group"
                >
                    <div className={`p-1 rounded-md transition-all ${showRules ? 'bg-neon/20 text-neon' : 'bg-white/10 text-white/40 group-hover:text-white'}`}>
                        <ShieldCheck size={14} className={showRules ? 'animate-pulse' : ''} />
                    </div>
                    <span>{showRules ? 'Hide' : 'Show'} Strategy Conditions & Rules</span>
                    <div className={`ml-auto transition-transform duration-300 ${showRules ? 'rotate-180 text-neon' : ''}`}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
                    </div>
                </button>

                {showRules && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-3 shadow-lg">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="p-1.5 rounded-lg bg-neon/10 text-neon">
                                    <ShieldCheck size={16} />
                                </div>
                                <h4 className="text-xs font-bold uppercase tracking-wider text-white/90">Entry Conditions</h4>
                            </div>
                            <ul className="space-y-2">
                                <li className="flex items-start gap-2 text-[11px] text-muted leading-relaxed">
                                    <span className="mt-1 w-1 h-1 rounded-full bg-neon/50 shrink-0" />
                                    <span>Price deviation from <b className="text-white/80">Daily Open</b> exceeds ATR threshold</span>
                                </li>
                                <li className="flex items-start gap-2 text-[11px] text-muted leading-relaxed">
                                    <span className="mt-1 w-1 h-1 rounded-full bg-neon/50 shrink-0" />
                                    <span>Variable thresholds for <b className="text-white/80">First</b> vs <b className="text-white/80">Second Window</b></span>
                                </li>
                                <li className="flex items-start gap-2 text-[11px] text-muted leading-relaxed">
                                    <span className="mt-1 w-1 h-1 rounded-full bg-neon/50 shrink-0" />
                                    <span>Entry via <b className="text-white/80">Bar Close</b> (H/HH) or <b className="text-white/80">Traps</b></span>
                                </li>
                            </ul>
                        </div>

                        <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-3 shadow-lg">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400">
                                    <AlertCircle size={16} />
                                </div>
                                <h4 className="text-xs font-bold uppercase tracking-wider text-white/90">Strategy Rules</h4>
                            </div>
                            <ul className="space-y-2">
                                <li className="flex items-start gap-2 text-[11px] text-muted leading-relaxed">
                                    <span className="mt-1 w-1 h-1 rounded-full bg-amber-400/50 shrink-0" />
                                    <span>Positions liquidated at <b className="text-white/80">15:30 ET</b> (EOD)</span>
                                </li>
                                <li className="flex items-start gap-2 text-[11px] text-muted leading-relaxed">
                                    <span className="mt-1 w-1 h-1 rounded-full bg-amber-400/50 shrink-0" />
                                    <span>Daily limit of <b className="text-white/80">1 trade</b> per symbol (default)</span>
                                </li>
                                <li className="flex items-start gap-2 text-[11px] text-muted leading-relaxed">
                                    <span className="mt-1 w-1 h-1 rounded-full bg-amber-400/50 shrink-0" />
                                    <span>No entries after <b className="text-white/80">Last Entry Time</b> cutoff</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                )}
            </div>

            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-foreground">
                {levelsDate && (
                    <div className="rounded-xl bg-white/6 border border-white/10 px-3 py-2 flex items-center justify-between">
                        <span className="text-muted mr-2">Latest Data</span>
                        <span className="text-foreground font-semibold">{levelsDate}</span>
                    </div>
                )}
            </div>

            {hasData && (
                <div className="mt-4 rounded-2xl bg-white/6 text-sm text-foreground">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-neon">Symbol Status</h3>
                        {dtStatus.data?.config_source && (
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setShowConfigModal(true)}
                                    className={`inline-flex min-h-7 shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${dtStatus.data.config_source === 'USER'
                                    ? 'bg-neon/10 text-neon border border-neon/20'
                                    : 'bg-white/5 text-muted border border-white/10'
                                    } hover:bg-white/10 hover:text-white`}
                                    title="View current ATR-DT strategy setup"
                                >
                                    <Eye className="h-3.5 w-3.5" />
                                    <span className="whitespace-nowrap">{dtStatus.data.config_source === 'USER' ? 'User Config' : 'System Default'}</span>
                                    {dtStatus.data.config_version && <span className="whitespace-nowrap">v{dtStatus.data.config_version}</span>}
                                </button>
                            </div>
                        )}
                    </div>

                    <div ref={tableViewportRef} className="overflow-x-auto scrollbar-thin rounded-xl border border-white/5 bg-surface">
                        <table className="min-w-full divide-y divide-white/10 text-[13px] text-foreground">
                            <thead className="bg-black/60">
                                <tr>
                                    <th scope="col" className="px-4 py-4 text-left font-bold text-white/90 uppercase tracking-wider">Symbol</th>
                                    <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">Price</th>
                                    <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">Open</th>
                                    <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">ATR</th>
                                    <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">Threshold</th>
                                    <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">VWAP</th>
                                    <th scope="col" className="px-4 py-4 text-left font-bold text-white/90 uppercase tracking-wider">Window</th>
                                    <th scope="col" className="px-4 py-4 text-left font-bold text-white/90 uppercase tracking-wider text-[11px]">Cutoff</th>
                                    <th scope="col" className="px-4 py-4 text-left font-bold text-white/90 uppercase tracking-wider">Mode</th>
                                    <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">SL Factor</th>
                                    <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">Size</th>
                                    <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">Lev</th>
                                    <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">Trades</th>
                                    <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">Position</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {dtStatus.data?.symbol_status && Object.keys(dtStatus.data?.symbol_status || {}).length > 0 ? (
                                    Object.entries(dtStatus.data?.symbol_status || {}).map(([symbol, status]: [string, any]) => (
                                        <DTSymbolRow
                                            key={symbol}
                                            symbol={symbol}
                                            status={status}
                                            levelsDateIso={levelsDateIso}
                                            expanded={expandedSymbol === symbol}
                                            onToggle={() => setExpandedSymbol((current) => current === symbol ? null : symbol)}
                                            chartWidth={chartWidth}
                                            marketOpen={marketOpen}
                                        />
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={14} className="px-3 py-4 text-center text-muted">
                                            {dtEnabled ? "Waiting for market data..." : "Strategy offline"}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            <Modal
                isOpen={showConfigModal}
                onClose={() => setShowConfigModal(false)}
                title="ATR-DT Strategy Setup"
                width="max-w-4xl"
            >
                <StrategyConfigPanel strategyName="DT" readOnly />
            </Modal>
        </>
    );
};

export default DTStrategyContent;
